import React, { useEffect, useMemo, useRef, useState } from "react";

import { GalleryImage } from "./GalleryImage";
import { getFileUrl } from "../../core/config/api.js";
import { Icon, IconButton } from "../../components/ui";
import "./gallery.css";

const getDirectionToIndex = (from, to, count) => {
  if (from === to) {
    return null;
  }

  const forward = (to - from + count) % count;
  const backward = (from - to + count) % count;

  return forward <= backward ? "next" : "prev";
};

const isAdjacentIndex = (from, to, count) => {
  if (from === to) {
    return false;
  }

  return Math.abs(from - to) === 1 || (from === 0 && to === count - 1) || (from === count - 1 && to === 0);
};

const ProductPreviewGallery = ({
  images = [],
  className = "",
  onSlide,
  topRightOverlay,
  badges = null,
  showThumbnails = false,
  thumbnailPosition = "left",
  slideDuration = 450,
}) => {
  const [index, setIndex] = useState(0);
  const [transition, setTransition] = useState(null);
  const indexRef = useRef(0);

  const slides = useMemo(
    () =>
      (images || []).map((img) => ({
        id: img.id,
        src: getFileUrl(img.id),
      })),
    [images?.map((img) => img.id).join(",")]
  );

  const slideIds = slides.map((slide) => slide.id).join(",");
  const count = slides.length;

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    setIndex(0);
    setTransition(null);
    indexRef.current = 0;
  }, [slideIds]);

  const startTransition = (from, to, direction) => {
    setTransition({ direction, from, to });
  };

  const navigate = (direction) => {
    if (count <= 1 || transition) {
      return;
    }

    const from = indexRef.current;
    const to =
      direction === "next"
        ? (from + 1) % count
        : (from - 1 + count) % count;

    startTransition(from, to, direction);
  };

  const goToIndex = (targetIndex) => {
    if (count <= 1 || transition || targetIndex === indexRef.current) {
      return;
    }

    const from = indexRef.current;

    if (isAdjacentIndex(from, targetIndex, count)) {
      startTransition(from, targetIndex, getDirectionToIndex(from, targetIndex, count));
      return;
    }

    setIndex(targetIndex);
    indexRef.current = targetIndex;
    onSlide?.(targetIndex);
  };

  useEffect(() => {
    if (!transition) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIndex(transition.to);
      indexRef.current = transition.to;
      onSlide?.(transition.to);
      setTransition(null);
    }, slideDuration);

    return () => window.clearTimeout(timer);
  }, [transition, slideDuration, onSlide]);

  if (!count) {
    return null;
  }

  const showNav = count > 1;
  const activeIndex = transition ? transition.from : index;
  const thumbPositionClass =
    showThumbnails && thumbnailPosition === "bottom"
      ? "image-gallery-thumbnails-bottom"
      : "image-gallery-thumbnails-left";

  return (
    <div
      className={`gallery gallery--dao gallery--preview-slide preview-gallery ${className}`}
      style={{
        position: "relative",
        "--gallery-slide-duration": `${slideDuration}ms`,
      }}
    >
      {badges}
      {topRightOverlay}
      <div
        className={`preview-gallery__layout image-gallery-content${
          showThumbnails ? ` ${thumbPositionClass}` : ""
        }`}
      >
        {showThumbnails && (
          <div className="preview-gallery__thumbnails-wrapper image-gallery-thumbnails-wrapper">
            <div className="preview-gallery__thumbnails image-gallery-thumbnails">
              <div className="image-gallery-thumbnails-container">
                {slides.map((slide, slideIndex) => (
                  <button
                    key={slide.id}
                    type="button"
                    className={`image-gallery-thumbnail${
                      slideIndex === index ? " active" : ""
                    }`}
                    onClick={() => goToIndex(slideIndex)}
                    aria-label={`Show image ${slideIndex + 1}`}
                    aria-current={slideIndex === index ? "true" : undefined}
                  >
                    <span className="image-gallery-thumbnail-inner">
                      <img
                        className="image-gallery-thumbnail-image"
                        src={slide.src}
                        alt=""
                      />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="preview-gallery__stage image-gallery-slide-wrapper">
          {showNav && (
            <>
              <IconButton
                round
                size="lg"
                label="Previous image"
                className="preview-gallery__nav preview-gallery__nav--prev"
                onClick={() => navigate("prev")}
                data-btn="gallery-prev"
              >
                <Icon name="ChevronLeft" size="md" />
              </IconButton>
              <IconButton
                round
                size="lg"
                label="Next image"
                className="preview-gallery__nav preview-gallery__nav--next"
                onClick={() => navigate("next")}
                data-btn="gallery-next"
              >
                <Icon name="ChevronRight" size="md" />
              </IconButton>
            </>
          )}
          <div className="preview-gallery__viewport image-gallery-slides">
            {transition ? (
              <div
                className={`preview-gallery__track preview-gallery__track--${transition.direction}`}
              >
                <div className="preview-gallery__slide preview-gallery__slide--outgoing image-gallery-slide">
                  <GalleryImage config={{ original: slides[transition.from].src }} />
                </div>
                <div className="preview-gallery__slide preview-gallery__slide--incoming image-gallery-slide">
                  <GalleryImage config={{ original: slides[transition.to].src }} />
                </div>
              </div>
            ) : (
              <div className="preview-gallery__slide preview-gallery__slide--active image-gallery-slide">
                <GalleryImage config={{ original: slides[activeIndex].src }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPreviewGallery;
